import { useTranslation } from 'react-i18next';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, ClickAwayListener, IconButton, MenuItem, Paper, TextField, Tooltip, Typography } from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTokens } from 'theme';
import ComposerTextarea from './ComposerTextarea';
import MessageReference from './MessageReference';
import ModelPicker from './ModelPicker';

/**
 * Question input: Enter sends, Shift+Enter breaks the line. A refused send
 * keeps the text so the user can fix it; `error` shows under the field.
 * `models` / `choice` / `onChoice` drive the picker; with no models the
 * workspace has no connected provider yet and nothing can be sent.
 */
export default function Composer({ onSend, onStop, stopping = false, disabled, busy, error, models = [], families = [], family, choice, onChoice, skills = [], skill, onSkill, diagnostic = false, onDiagnostic, reply, onCancelReply, focusToken }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const inputRef = useRef(null);
  const pickerId = useId();
  // Keystrokes only update this small subtree, never the conversation/charts.
  const [value, onChange] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dismissed, setDismissed] = useState(null);
  const [highlight, setHighlight] = useState(0);
  useEffect(() => {
    if (focusToken) inputRef.current?.focus({ preventScroll: true });
  }, [focusToken]);
  const noModels = models.length === 0;
  const off = disabled || noModels;
  const canSend = !off && !busy && (value.trim() !== '' || Boolean(skill));
  const slash = /^\/[a-z0-9_-]*$/i.test(value);
  const matches = skills.filter((s) => !slash || s.command.toLowerCase().startsWith(value.toLowerCase()));
  const open = !off && !busy && (pickerOpen || (slash && dismissed !== value)) && matches.length > 0;
  const choose = (next) => {
    onSkill?.(next.id);
    if (slash) onChange('');
    setPickerOpen(false);
    setDismissed(value);
    setHighlight(0);
    inputRef.current?.focus({ preventScroll: true });
  };

  const send = async () => {
    if (!canSend) return;
    const submitted = value;
    if (await onSend(value.trim() ? value : skill.command)) {
      // Keep anything the user started typing while the request was accepted.
      onChange((current) => current === submitted ? '' : current);
    }
  };

  let placeholder = translate('assistant.composerAskAboutYourDataForExampleHow');
  if (skill) placeholder = skill.input_hint || (skill.result_kind === 'dashboard'
    ? translate('assistant.composerDescribeTheDashboardTopicMetricsPeriodAnd')
    : translate('assistant.composerAskAboutYourDataMetricsPeriodAnd'));
  if (disabled) placeholder = translate('assistant.composerAssistantUnavailable');
  else if (noModels) placeholder = translate('assistant.composerNoModelsAvailableAnOwnerOrAdministrator');

  return (
    <Box sx={{ pt: 1.5 }}>
      <ClickAwayListener onClickAway={() => { setPickerOpen(false); setDismissed(value); }}>
      <Box sx={{
        position: 'relative', border: '1px solid', borderColor: error ? t.negative : t.border,
        borderRadius: 3, bgcolor: t.surface, p: 1.5, transition: 'border-color 180ms ease, box-shadow 180ms ease',
        '&:focus-within': { borderColor: error ? t.negative : t.focus, boxShadow: `0 0 0 3px ${t.focus}33` },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}>
      {skill && (
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={`${skill.command} · ${skill.title}`}
          onDelete={!off && !busy ? () => onSkill?.(null) : undefined}
          sx={{ mb: 0.5, ml: 1, maxWidth: 'calc(100% - 16px)' }}
        />
      )}
      {reply && <Box sx={{ mb: 1 }}><MessageReference reference={reply} onCancel={onCancelReply} /></Box>}
        {open && (
          <Paper
            variant="outlined"
            role="listbox"
            id={pickerId}
            aria-label={translate('assistant.composerTools')}
            sx={{ position: 'absolute', bottom: 'calc(100% + 12px)', left: 0, width: 420, maxWidth: '100%', zIndex: 2, borderRadius: 3, p: 0.75, maxHeight: 'min(320px, 50vh)', overflowY: 'auto', boxShadow: 4 }}
          >
            <Typography variant="subtitle2" sx={{ px: 1.5, py: 1 }}>{translate('assistant.composerTools')}</Typography>
            {matches.map((s, i) => (
              <MenuItem key={s.id} id={`${pickerId}-${i}`} role="option" selected={i === highlight % matches.length} aria-selected={i === highlight % matches.length} onMouseEnter={() => setHighlight(i)} onClick={() => choose(s)} sx={{ display: 'block', whiteSpace: 'normal', borderRadius: 2, my: 0.5, py: 1.25 }}>
                <Typography variant="body2" fontWeight={700}>{s.command} · {s.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{s.description}</Typography>
              </MenuItem>
            ))}
          </Paper>
        )}
        <TextField
          fullWidth
          multiline
          size="small"
          variant="standard"
          placeholder={placeholder}
          inputRef={inputRef}
          value={value}
          disabled={off}
          onChange={(e) => { setHighlight(0); onChange(e.target.value); }}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (open && ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key) && !(e.key === 'Enter' && e.shiftKey)) {
              e.preventDefault();
              if (e.key === 'Escape') { setPickerOpen(false); setDismissed(value); }
              else if (e.key === 'Enter' && !e.shiftKey) choose(matches[highlight % matches.length]);
              else if (e.key !== 'Enter') setHighlight((i) => (i + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
              return;
            }
            if (e.key === 'Escape' && reply && onCancelReply) {
              e.preventDefault();
              onCancelReply();
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
          InputProps={{ inputComponent: ComposerTextarea, disableUnderline: true }}
          inputProps={{ 'aria-label': translate('assistant.composerQuestionForTheAssistant'), maxRows: 6, ...(open ? { 'aria-controls': pickerId, 'aria-activedescendant': `${pickerId}-${highlight % matches.length}` } : {}) }}
          sx={{ '& .MuiInputBase-root': { px: 1, pt: 1, pb: 2 }, '& textarea::placeholder': { color: t.inkMuted, opacity: 1 } }}
        />
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5, pt: 1.5, borderTop: `1px solid ${t.border}` }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <ModelPicker models={models} families={families} family={family} choice={choice} onChoice={onChoice} disabled={off || busy} busy={busy} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {skills.length > 0 && (
          <Tooltip title={translate('assistant.composerChooseAToolOrTypeInYour')}>
            <span>
              <Button size="small" color="inherit" disabled={off || busy}
                startIcon={<AutoAwesomeIcon fontSize="small" />} onClick={() => { setPickerOpen((v) => !v); setHighlight(0); inputRef.current?.focus({ preventScroll: true }); }}
                aria-label={translate('assistant.composerTools')} aria-haspopup="listbox" aria-expanded={open}
                sx={{ borderRadius: 2, px: 1.5, minHeight: 36, color: skill || open ? t.link : t.inkMuted, bgcolor: skill || open ? `${t.highlight}` : 'transparent' }}>{translate('assistant.composerTools2')}</Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title={translate('assistant.composerShowProgressIntermediateMessagesAndActionResults')}>
          <span>
            <IconButton size="small" aria-label={translate('assistant.composerShowReasoningAndResults')} aria-pressed={diagnostic}
              disabled={off || busy} onClick={() => onDiagnostic?.(!diagnostic)}
              sx={{ width: 36, height: 36, borderRadius: 2, color: diagnostic ? t.link : t.inkMuted, bgcolor: diagnostic ? `${t.highlight}` : 'transparent' }}>
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        </Box>
        </Box>
        <Tooltip title={onStop ? (stopping ? translate('assistant.composerStoppingAnswer') : translate('assistant.composerStopAnswer')) : busy ? translate('assistant.composerSendingQuestion') : translate('assistant.composerSend')}>
          <span>
            <IconButton onClick={onStop || send} disabled={onStop ? stopping : !canSend} aria-label={onStop ? translate('assistant.composerStopAnswer') : translate('assistant.composerSend')} sx={{
              width: 38, height: 38, borderRadius: 2.5, bgcolor: t.accent, color: t.onAccent,
              '&:hover': { bgcolor: t.accentHover }, '&.Mui-disabled': { bgcolor: t.raised, color: t.inkDim },
            }}>
              {onStop ? (stopping ? <CircularProgress size={18} color="inherit" aria-label={translate('assistant.composerStoppingAnswer2')} /> : <StopRoundedIcon />)
                : busy ? <CircularProgress size={18} color="inherit" aria-label={translate('assistant.composerSendingQuestion2')} /> : <ArrowUpwardRoundedIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      </Box>
      </ClickAwayListener>
      <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, color: t.inkMuted, mt: 0.75, textAlign: 'right', fontSize: '0.7rem' }}>{translate('assistant.composerEnterSendShiftEnterNewLine')}</Typography>
      {error && (
        <Typography variant="caption" role="alert" sx={{ display: 'block', color: t.negative, mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
